import React from 'react';

import clsx from 'clsx';

import { useLocale, useId } from '@coursera/cds-common';
import type { BaseComponentProps } from '@coursera/cds-common';

import { Modal } from '@core/Modal';
import type { ModalProps as BaseModalProps } from '@core/Modal';
import type { StickyType } from '@core/StickyScroll';
import { StickyScroll } from '@core/StickyScroll';

import getTemporaryDrawerCss, { classes } from './styles/getTemporaryDrawerCss';
import { TemporaryDrawerContextProvider } from './TemporaryDrawerContextProvider';

export type Anchor = 'left' | 'right';
export type ModalProps = Omit<BaseModalProps, 'classes' | 'transition'>;

export type Props = {
  /**
   * Sets the side from which the drawer will appear.
   * Will automatically flip for RTL direction.
   * @default left
   */
  anchor?: Anchor;

  /**
   * Use TemporaryDrawer sub-components to compose the drawer:
   *
   * ```jsx
   * <TemporaryDrawer>
   *   <TemporaryDrawer.HeadingGroup/>
   *   <TemporaryDrawer.Content />
   *   <TemporaryDrawer.Actions />
   * </TemporaryDrawer>
   * ```
   *
   * @see TemporaryDrawer.HeadingGroup
   * @see TemporaryDrawer.Content
   * @see TemporaryDrawer.Actions
   */
  children?: React.ReactNode | React.ReactNode[];

  /**
   * Desired scroll behavior for the heading group or actions when drawer content reaches
   * max height.
   *
   * @default headingGroup
   */
  sticky?: StickyType;

  /**
   * Sets the desired width behavior of the drawer.
   *
   * @default medium
   */
  width?: 'medium' | 'large';
  /**
   * Whether to add padding to the nested Content.
   * @default true
   */
  contentPadding?: boolean;
} & ModalProps &
  BaseComponentProps<'div'>;

const transitionMapLtr: Record<Anchor, BaseModalProps['transition']> = {
  left: 'slide-right',
  right: 'slide-left',
};

const transitionMapRtl: Record<Anchor, BaseModalProps['transition']> = {
  left: 'slide-left',
  right: 'slide-right',
};

const getHeadingGroupId = (node: React.ReactNode, baseId?: string) => {
  const configuredId = React.isValidElement(node) ? node.props.id : undefined;
  return configuredId || `${baseId}-headingGroup`;
};

const TemporaryDrawer = React.forwardRef(function TemporaryDrawer(
  props: Props,
  ref: React.Ref<HTMLDivElement>
) {
  const {
    id: idProp,
    className,
    open,
    anchor = 'left',
    sticky = 'headingGroup',
    width = 'medium',
    contentPadding = true,
    onClose,
    children,
    initialFocusRef,
    returnFocusRef,

    disableFocusLock,
    renderInPlace,
    ...restHtmlAttr
  } = props;

  const id = useId(idProp);

  const [headingGroup] = React.Children.toArray(children);
  const headingGroupId = getHeadingGroupId(headingGroup, id);

  const { direction } = useLocale();

  const transition =
    direction === 'ltr' ? transitionMapLtr[anchor] : transitionMapRtl[anchor];

  const totalChildren = React.Children.toArray(children).length;

  return (
    <Modal
      ref={ref}
      disableFocusLock={disableFocusLock}
      id={id}
      initialFocusRef={initialFocusRef}
      open={open}
      renderInPlace={renderInPlace}
      returnFocusRef={returnFocusRef}
      transition={transition}
      onClose={onClose}
    >
      <div
        aria-modal
        aria-labelledby={headingGroupId}
        className={clsx(className, {
          [classes.widthMedium]: width === 'medium',
          [classes.widthLarge]: width === 'large',
          [classes.anchorLeft]: anchor === 'left',
          [classes.anchorRight]: anchor === 'right',
          [classes.stickyNone]: sticky === 'none',
          [classes.stickyHeadingGroup]: sticky === 'headingGroup',
          [classes.stickyActions]: sticky === 'actions',
          [classes.notPaddedContent]: !contentPadding,
          [classes.noActionsSection]: totalChildren === 2,
        })}
        css={getTemporaryDrawerCss}
        role="dialog"
        {...restHtmlAttr}
      >
        <TemporaryDrawerContextProvider
          value={{
            onClose: onClose,
            sticky: sticky,
            id,
            headingGroupId,
          }}
        >
          <div className={classes.container}>
            <StickyScroll className={classes.scrollContainer} sticky={sticky}>
              {children}
            </StickyScroll>
          </div>
        </TemporaryDrawerContextProvider>
      </div>
    </Modal>
  );
});

export default TemporaryDrawer;
